import React, { useState, useEffect, JSXElementConstructor, Key, ReactElement } from "react";
// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
// Material Dashboard 2 PRO React TS exampless
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
// Material Dashboard 2 PRO React TS themes
import theme from "assets/theme";
// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "assets/theme-dark";
import { withBoldText } from "assets/theme/boldText";
// Material Dashboard 2 PRO React TS routes
import routes from "routes";
// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController, setMiniSidenav } from "context";
import { useLoadBoldTextSetting } from "context/boldTextSetting";
// Images
import logo from "assets/images/small-logos/RCXfavicon.jpg";

import { EmployeeRouter } from "employee/components/EmployeeRouter";
import { TripRouter } from "trips/TripRouter";
import { VehicleRouter } from "vehicles/components/VehicleRouter";
import { HubsRouter } from "hubs/components/HubsRouter";
import { OrderManagementRouter} from "orderManagement/components/OrderManagementRouter";
import { ReviewQueueRouter } from "reviewQueue/ReviewQueueRouter";
import { ShiftsRouter } from "shifts/ShiftsRouter";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ProtectedRoute from "common/ProtectedRoute";
import { Role } from "common/Constants";
import { AdminRouter } from "admin/components/AdminRouter";
import Home from "home/components/Home";
import { MapsRouter } from "map/components/MapsRouter";
import VehicleHistory from "map/components/VehicleHistory";
import HowsMyDriving from "selfService/howsMyDriving/howsMyDriving";
import Invoicing from "finance/components/invoicing";
import AdHocProcesses from "finance/components/AdHocProcesses";
import BnsfFollowUps from "finance/components/BnsfFollowUps";
import { PointsRouter } from "points/PointsRouter";
import { PointPermission } from "points/permissions";
import { FinancePermission } from "finance/permissions";
import {ClientsRouter} from "clients/ClientsRouter";
import { FormsRouter } from "forms/components/FormsRouter";
import { ReportsRouter } from "reports/ReportsRouter";
import TripDetailsReport from "reports/components/TripDetailsReport";
import WhatsNewDialog from "common/components/WhatsNewDialog";

// Light theme for users who turned on "Bold, darker text" in the Configurator.
const themeBold = withBoldText(theme);

const MainLayout = () => {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode, boldText } =
    controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
    const { pathname } = useLocation();
  useLoadBoldTextSetting();

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Popup map route: no sidebar, no navbar, just the map
  if (pathname.startsWith("/maps/popup")) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : boldText ? themeBold : theme}>
        <CssBaseline />
        <MDBox mx={2} mt={1} sx={{ height: "calc(100vh - 16px)" }}>
          <VehicleHistory filterOnly />
        </MDBox>
      </ThemeProvider>
    );
  }

  // Popup report route: chromeless viewer, opened via window.open from elsewhere in the app
  if (pathname.startsWith("/reports/popup")) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : boldText ? themeBold : theme}>
        <CssBaseline />
        <MDBox sx={{ height: "100vh" }}>
          <Routes>
            <Route path="/reports/popup/trip-details/:tripId" element={<TripDetailsReport />} />
          </Routes>
        </MDBox>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : boldText ? themeBold : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? logo : logo}
            brandName="Flow"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
        </>
      )}
      <WhatsNewDialog />
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={0} pt={0} pb={3}>
          <Routes>
            <Route path="/order-management/*" element={<ProtectedRoute roles={[Role.Admin, Role.TripAdmin, Role.Dispatcher]}><OrderManagementRouter/></ProtectedRoute>}/>
            <Route path="/review-queue/*" element={<ProtectedRoute roles={[Role.Admin, Role.TripAdmin]}><ReviewQueueRouter/></ProtectedRoute>}/>
            <Route path="/trips/*" element={<ProtectedRoute roles={[Role.Admin, Role.TripAdmin]}><TripRouter /></ProtectedRoute>} />
            {/* Permission only, matching the menu. Roles are deliberately not listed here: they
                would union with the permission and let anyone holding the role reach /shifts by
                URL, which would make revoking AccessShifts do nothing. */}
            <Route path="/shifts/*" element={<ProtectedRoute permissions={["AccessShifts"]}><ShiftsRouter /></ProtectedRoute>} />
            <Route path="/employees/*" element={<ProtectedRoute roles={[Role.Admin, Role.DriverAdmin]}><EmployeeRouter /></ProtectedRoute>} />
            <Route path="/vehicles/*" element={<ProtectedRoute roles={[Role.Admin, Role.VehicleAdmin]}><VehicleRouter /></ProtectedRoute>} />
            <Route path="/hubs/*" element={<ProtectedRoute roles={[Role.Admin, Role.HubAdmin]}><HubsRouter /></ProtectedRoute>} />
            <Route path="/clients/*" element={<ProtectedRoute roles={[Role.Admin, Role.HubAdmin]}><ClientsRouter /></ProtectedRoute>} />
            <Route path="/admin/*" element={<ProtectedRoute roles={[Role.Admin]}><AdminRouter /></ProtectedRoute>} />
            <Route path="/forms/*" element={<FormsRouter />} />
            <Route path="/maps/*" element={<MapsRouter/>}/>
            <Route path="/howsMyDriving/*" element={<HowsMyDriving />}/>
            <Route path="finance/invoicing/*" element={<ProtectedRoute roles={[Role.Admin, Role.FinanceAdmin]}><Invoicing/></ProtectedRoute>}/>
            <Route path="finance/adhoc-processes/*" element={<ProtectedRoute roles={[Role.Admin, Role.FinanceAdmin]}><AdHocProcesses/></ProtectedRoute>}/>
            {/* Permission only, matching the menu, same reasoning as /shifts/* above: roles are
                deliberately not listed here, so revoking AccessBnsfFollowUps actually revokes
                the page instead of a role union leaving it reachable by URL. */}
            <Route path="finance/bnsf-followups/*" element={<ProtectedRoute permissions={[FinancePermission.AccessBnsfFollowUps]}><BnsfFollowUps/></ProtectedRoute>}/>
            {/* CreatePoint is listed here so a LocationAdmin holding it can reach the screen it
                belongs to at all -- the role is not in the list above, so the outer gate would
                otherwise turn them away before PointsRouter's own gate ran. */}
            <Route path="points/*" element={<ProtectedRoute roles={[Role.Admin, Role.TripAdmin, Role.HubAdmin]} permissions={[PointPermission.CreatePoint]}><PointsRouter/></ProtectedRoute>}/>
            <Route path="reports/*" element={<ProtectedRoute roles={[Role.Admin, Role.TripAdmin]}><ReportsRouter /></ProtectedRoute>}/>
            <Route path="*" element={<Home/>} />
          </Routes>
        </MDBox>
      </DashboardLayout>
    </ThemeProvider>
  );
};
export default MainLayout;
